import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Container,
    CircularProgress,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { TopBar } from '../layout/TopBar';
import { PostCard } from '../components/social/PostCard';
import { getPostById } from '../services/socialService';
import { useSocial } from '../hooks/useSocial';
import { Post } from '../types/social';

export const PostDetailsPage: React.FC = () => {
    const { theme } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const { postId } = useParams<{ postId: string }>();
    const [post, setPost] = useState<Post | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const {
        toggleLike,
        checkLike,
        toggleFavorite,
        checkFavorite,
        addPostComment,
        getComments,
        removePost,
    } = useSocial();

    useEffect(() => {
        const fetchPost = async () => {
            if (!postId) {
                setError('Post não encontrado');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const fetchedPost = await getPostById(postId);

                if (!fetchedPost) {
                    setError('Post não encontrado ou foi removido');
                } else {
                    setPost(fetchedPost);
                }
            } catch (err) {
                console.error('Erro ao buscar post:', err);
                setError('Erro ao carregar o post');
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
    }, [postId]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: theme.colors.background }}>
                <CircularProgress sx={{ color: theme.colors.primary }} />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: theme.colors.background, pb: 4 }}>
            <TopBar
                title="Post"
                showBackButton={true}
                onBackClick={() => navigate(-1)}
            />

            <Container maxWidth="sm" sx={{ pt: '70px', px: 0 }}>
                {error ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ color: theme.colors.text.secondary }}>
                            {error}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: theme.colors.primary, mt: 2, cursor: 'pointer', fontWeight: 'bold' }}
                            onClick={() => navigate('/hostly-club')}
                        >
                            Voltar para o HostlyClub
                        </Typography>
                    </Box>
                ) : post && (
                    <PostCard
                        post={post}
                        onLike={toggleLike}
                        onFavorite={toggleFavorite}
                        onComment={addPostComment}
                        onDelete={post.userId === user?.uid ? removePost : undefined}
                        checkLike={checkLike}
                        checkFavorite={checkFavorite}
                        getComments={getComments}
                    />
                )}
            </Container>
        </Box>
    );
};
